import React, { useContext, useEffect, useRef, useState } from 'react'
import Heading from '@components/Heading'
import {
    HEADING_TYPE,
    DIVIDER_SIZE,
    BUTTON_TYPE,
} from '@helpers/constants'
import Button from '@components/Button'
import Divider from '@components/Divider'
import { nanoid } from 'nanoid'
import { renderToStaticMarkup } from 'react-dom/server'
import { connectToEditorjs } from '@blocks/connectToEditorjs'
import Icon from '@components/Icon'
import Form from '@components/Form'
import EditExerciseWrapper from '@components/EditExerciseWrapper'
import { AppContext } from '@state/context'
import { addOnSaveCallback } from '@state/actions/editorActions'
import { getImageSrc } from '@helpers/utilities'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'

/**
 * Quiz questions
 * @returns
 */
const QuizComponent = (props) => {
    const {
        initialState,
        handleStateChange,
        onSave,
        id,
    } = props
    const [_, dispatch] = useContext(AppContext)
    const [state, setState] = useState(initialState)
    const {
        name,
        preamble = '{}',
        image = null,
        type,
        questions = [],
    } = state

    if (!type) {
        setState({
            ...state,
            type: 'quizQuestions'
        })
    }

    const [scrollTo, setScrollTo] = useState(undefined)

    // the panel scrolls when moving a question, so we scroll back
    useEffect(() => {
        if (scrollTo) {
            ref.current.parentNode.scrollTo({top: scrollTo})

            setScrollTo(undefined)
        }
    }, [scrollTo])

    const ref = useRef()

    useEffect(() => {
      dispatch(
        addOnSaveCallback({
          id,
          callback: async () => {
            let newImage = await (async () => {
              if (!(image?.previewUrl instanceof File)) return image
              const filename = await uploadImage(image.previewUrl)

              return { filename: `${storagePath}/${filename}` }
            })()

            handleStateChange({
              ...state,
              image: newImage,
            })
          },
        })
      )
    }, [state])

    /**
     * Broadcast state change up
     */
    useEffect(() => {
        handleStateChange(state)
    }, [state])

    /**
     * Remove question
     * @param {*} questionKey
     */
    const removeQuestion = (questionKey) => {
        setState({
            ...state,
            questions: questions.filter((q, k) => k !== questionKey),
        })
    }

    const moveQuestion = (fromIndex, toIndex) => {
        setScrollTo(ref.current.parentNode.scrollTop)

        const element = questions[fromIndex]
        questions.splice(fromIndex, 1)
        questions.splice(toIndex, 0, element)

        setState({
            ...state,
            questions: [...questions]
        })
    }

    /**
     * Add new question
     */
    const addNewQuestion = () => {
        setState({
            ...state,
            questions: [
                ...questions,
                {
                    id: nanoid(),
                    question: '{}',
                    answers: [
                        { text: '{}', correct: true, textArea: '{}' },
                        { text: '{}', correct: false, textArea: '{}' },
                        { text: '{}', correct: false, textArea: '{}' },
                    ],
                },
            ],
        })
    }

    /**
     * Update question
     * @param {*} questionKey
     * @param {*} key
     * @param {*} value
     * @param {*} answerKey
     */
    const updateQuestion = (questionKey, key, value, answerKey = false) => {
        const newQuestions = questions.map((question, qKey) => {
            if (questionKey === qKey) {
                // If updating answer under question?
                if (answerKey !== false) {
                    question.answers = question.answers.map((answer, aKey) => {
                        // Reset correct
                        if (key === 'correct') {
                            answer.correct = false
                            answer.textArea = '{}'
                        }

                        if (answerKey === aKey) {
                            answer[key] = value
                        }
                        return answer
                    })
                } else {
                    question[key] = value
                }
            }
            return question
        })
        setState({
            ...state,
            questions: newQuestions,
        })
    }

    return (
        <div ref={ref}>
            <EditExerciseWrapper
                onSave={onSave}
                type={type && type === 'quizQuestions'
                    ? 'Quiz - frågor'
                    : 'Quiz - läsa och förstå'
                }
            >
                <Heading headingLevel={HEADING_TYPE.H2}>
                    Välj quiztyp
                </Heading>
                <Button
                    marginRight
                    // disabled={type === 'quizQuestions'}
                    type={type === 'quizQuestions' ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY}
                    handleClick={() => {
                        setState({
                            ...state,
                            type: 'quizQuestions',
                        })
                    }}
                >
                    Quiz - Frågor
                </Button>
                <Button
                    // disabled={type === 'quizExercise'}
                    handleClick={() => {
                        setState({
                            ...state,
                            type: 'quizExercise',
                        })
                    }}
                    type={type === 'quizExercise' ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY}
                >
                    Quiz - läsa och förstå
                </Button>
                <Divider size={DIVIDER_SIZE.SMALL} />
                {/* ----- Name ----- */}
                <Heading
                    headingLevel={HEADING_TYPE.H3}
                >
                    Namn på övningen
                </Heading>
                <Form.Editor
                    content={name}
                    handleChange={(value) => setState({ ...state, name: value })}
                    elementId={`quiz-name-${id}`}
                />
                <Divider size={DIVIDER_SIZE.SMALL} />
                <Heading
                    headingLevel={HEADING_TYPE.H3}
                >
                    Inledande text
                </Heading>
                <Form.Editor
                    content={preamble}
                    handleChange={(value) => setState({ ...state, preamble: value })}
                    // placeholder={'Skriv en inledande text om övningen'}
                    elementId={`quiz-preamble-${id}`}
                />
                <Divider size={DIVIDER_SIZE.SMALL} />
                <Heading
                    headingLevel={HEADING_TYPE.H3}
                >
                    Bild
                </Heading>
                <Form.ImageInput
                    handleImageChange={(e) => {
                        e.preventDefault()
                        setState({
                        ...state,
                            image: { previewUrl: e.target.files[0] },
                        })
                    }}
                    id={'quiz-image'}
                    imagePreviewUrl={getImageSrc(image)}
                    inputKey={'inputKey'}
                    name={'image'}
                    onDelete={() => {
                        setState({...state, image: null})
                    }}
                />
                <Divider size={DIVIDER_SIZE.SMALL} />

                {/* ----- Questions ----- */}
                <ul>
                    {questions &&
                        questions.map((stateQuestion, stateKey) => {
                            const { question, answers = [] } = stateQuestion
                            if (!stateQuestion.id) {
                                updateQuestion(stateKey, 'id', nanoid())
                            }

                            return (
                                <li key={stateQuestion.id}>

                                    <Heading
                                        headingLevel={HEADING_TYPE.H3}
                                    >
                                        Fråga {stateKey + 1}
                                    </Heading>
                                    <Divider size={DIVIDER_SIZE.LINE} line />
                                    <Divider size={DIVIDER_SIZE.SMALL} />
                                    <Form.Editor
                                        content={question}
                                        handleChange={(value) => updateQuestion(
                                            stateKey,
                                            'question',
                                            value
                                        )}
                                        elementId={`quiz-question-${stateQuestion.id}`}
                                    />
                                    <Divider size={DIVIDER_SIZE.SMALL} />
                                    {type === 'quizQuestions' ?
                                        <p>Lägg till tre svarsalternativ, där ett av dem är det rätta. </p>
                                    :
                                        <p>Lägg till tre svarsalternativ, där ett
                                        av dem är det rätta. Glöm inte att
                                        markera rätt svar och hänvisa till
                                        aktuellt stycke i texten.</p>
                                    }
                                    <Divider size={DIVIDER_SIZE.SMALL} />
                                    <ul>
                                        {answers.map((answer, answerKey) => {
                                            if (!answer.id) {
                                                updateQuestion(
                                                    stateKey,
                                                    'id',
                                                    nanoid(8),
                                                    answerKey
                                                )

                                                return null
                                            }
                                            const {
                                                correct,
                                                text,
                                                textArea,
                                                id,
                                            } = answer
                                            return (
                                                <div key={id}>
                                                    <li
                                                        className={
                                                            'flex items-baseline'
                                                        }
                                                    >
                                                        <Form.Radio
                                                            type={'radio'}
                                                            name={`question-answer-${stateQuestion.id}`}
                                                            defaultChecked={
                                                                correct
                                                            }
                                                            onChange={(e) =>
                                                                updateQuestion(
                                                                    stateKey,
                                                                    'correct',
                                                                    e.target
                                                                        .value ==
                                                                        'on',
                                                                    answerKey
                                                                )
                                                            }
                                                        />
                                                        <div
                                                            className={
                                                                'flex flex-col w-full'
                                                            }
                                                        >
                                                            <Form.Label>Svarsalternativ {answerKey + 1}</Form.Label>
                                                            <Form.Editor
                                                                content={text}
                                                                handleChange={(value) =>
                                                                    updateQuestion(
                                                                        stateKey,
                                                                        'text',
                                                                        value,
                                                                        answerKey
                                                                    )
                                                                }
                                                                elementId={`quiz-answer-${id}`}
                                                            />

                                                            {correct &&
                                                                state.type ===
                                                                    'quizExercise' && (
                                                                    <>
                                                                        <Divider
                                                                            size={
                                                                                DIVIDER_SIZE.SMALL
                                                                            }
                                                                        />
                                                                        <Form.Editor
                                                                            content={textArea}
                                                                            handleChange={(value) =>
                                                                                updateQuestion(
                                                                                    stateKey,
                                                                                    'textArea',
                                                                                    value,
                                                                                    answerKey
                                                                                )
                                                                            }
                                                                            placeholder={'Förklaring'}
                                                                            elementId={`quiz-textArea-${id}`}
                                                                        />
                                                                    </>
                                                                )}

                                                            <Divider size={DIVIDER_SIZE.SMALL} />
                                                        </div>
                                                    </li>
                                                </div>
                                            )
                                        })}
                                    </ul>

                                    <Divider size={DIVIDER_SIZE.SMALL} />
                                    <Button
                                        type={BUTTON_TYPE.PRIMARY}
                                        handleClick={() => removeQuestion(stateKey)}
                                        marginRight
                                    >
                                        Ta bort fråga
                                    </Button>
                                    <Button
                                        type={BUTTON_TYPE.PRIMARY}
                                        disabled={stateKey === 0}
                                        handleClick={() => moveQuestion(stateKey, stateKey - 1)}
                                        marginRight
                                    >
                                        Flytta upp
                                    </Button>
                                    <Button
                                        type={BUTTON_TYPE.PRIMARY}
                                        disabled={stateKey + 1 === questions.length}
                                        handleClick={() => moveQuestion(stateKey, stateKey + 1)}
                                    >
                                        Flytta ner
                                    </Button>
                                    <Divider size={stateKey + 1 === questions.length ? DIVIDER_SIZE.SMALL : DIVIDER_SIZE.MEDIUM} />
                                </li>
                            )
                        })}
                </ul>
                {/* ----- Button ----- */}
                <Divider size={DIVIDER_SIZE.SMALL} />
                <Button
                    handleClick={addNewQuestion}
                    type={BUTTON_TYPE.PRIMARY}
                >
                    Lägg till en fråga
                </Button>
                <Divider size={DIVIDER_SIZE.MEDIUM} />
            </EditExerciseWrapper>
        </div>
    )
}

export const Quiz = connectToEditorjs(
    QuizComponent,
    'Quiz',
    renderToStaticMarkup(<Icon icon={'CheckMark'} />)
)
